import { useState, useEffect, useCallback } from "react";
import { t } from "@/lib/i18n";
import { Plus, Trash, PaperPlaneTilt, Copy, ListMagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { errDetail } from "@/lib/api";
import { MailboxSwitcher, useMailboxApi } from "@/components/MailboxSwitcher";

const EVENTS = ["email.received", "email.sent", "rule.matched", "followup.fired"];

export const WebhooksCard = () => {
  const { mailbox, setMailbox, mbApi } = useMailboxApi();
  const [hooks, setHooks] = useState([]);
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState({ "email.received": true, "email.sent": true, "rule.matched": false, "followup.fired": false });
  const [logFor, setLogFor] = useState(null);
  const [logs, setLogs] = useState([]);
  const load = useCallback(() => mbApi.get("/webhooks").then((r) => setHooks(r.data)).catch(() => {}), [mbApi]);
  useEffect(() => { load(); }, [load, mailbox]); // eslint-disable-line

  const add = async () => {
    const evs = EVENTS.filter((e) => events[e]);
    if (!url.trim()) { toast.error(t("wh.url_req", "Enter the destination URL")); return; }
    if (evs.length === 0) { toast.error(t("wh.event_req", "Pick at least one event")); return; }
    try {
      await mbApi.post("/webhooks", { url: url.trim(), events: evs });
      toast.success(t("wh.added", "Webhook added"));
      setUrl(""); load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const del = async (h) => {
    try { await mbApi.delete(`/webhooks/${h.id}`); toast.success(t("wh.removed", "Webhook removed")); if (logFor === h.id) setLogFor(null); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const test = async (h) => {
    const tid = toast.loading(t("wh.sending", "Sending test event…"));
    try {
      const { data } = await mbApi.post(`/webhooks/${h.id}/test`);
      toast.dismiss(tid);
      if (data.ok) toast.success(t("wh.test_ok", "Test delivered ✓ (HTTP {code})", { code: data.status_code }));
      else toast.error(t("wh.test_fail", "Test failed after {n} attempt(s): {err}", { n: data.attempts, err: data.error }));
      if (logFor === h.id) { const r = await mbApi.get(`/webhooks/${h.id}/deliveries`); setLogs(r.data); }
    } catch (e) { toast.dismiss(tid); toast.error(errDetail(e)); }
  };
  const toggleLog = async (h) => {
    if (logFor === h.id) { setLogFor(null); return; }
    try { const { data } = await mbApi.get(`/webhooks/${h.id}/deliveries`); setLogs(data); setLogFor(h.id); }
    catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <div className="mb-8" data-testid="webhooks-card">
      <MailboxSwitcher mailbox={mailbox} onChange={setMailbox} />
      <h2 className="font-heading font-bold text-gray-900 mb-1">{t("wh.title", "Outbound Webhooks")}</h2>
      <p className="text-sm text-gray-500 mb-4">{t("wh.desc", "Ring another app's doorbell the instant something happens — new email arrives, email sent, rule matched or follow-up fired. Payloads are HMAC-signed (timestamp.body, SHA-256).")}</p>
      <div className="border border-gray-200 rounded-md p-4 mb-3">
        <div className="flex gap-2 mb-3">
          <input data-testid="webhook-url-input" value={url} onChange={(e) => setUrl(e.target.value)}
            placeholder="https://hooks.zapier.com/…"
            className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <button data-testid="webhook-add-btn" onClick={add}
            className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-4 transition-colors"><Plus size={14} /> {t("wh.add", "Add")}</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {EVENTS.map((e) => (
            <label key={e} data-testid={`webhook-event-${e}`} className={`flex items-center gap-1.5 text-xs font-medium rounded-full border px-3 py-1.5 cursor-pointer transition-colors ${events[e] ? "bg-blue-50 border-blue-300 text-blue-700" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}>
              <input type="checkbox" className="hidden" checked={!!events[e]} onChange={() => setEvents({ ...events, [e]: !events[e] })} />
              {events[e] ? "✓ " : ""}{e}
            </label>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        {hooks.map((h) => (
          <div key={h.id} data-testid={`webhook-row-${h.id}`} className="border border-gray-200 rounded-md p-3">
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <code className="text-xs text-gray-800 break-all block">{h.url}</code>
                <div className="flex flex-wrap items-center gap-1 mt-1">
                  {(h.events || []).map((e) => (
                    <span key={e} className="text-[10px] font-semibold bg-gray-100 text-gray-600 rounded px-1.5 py-0.5">{e}</span>
                  ))}
                  <button data-testid={`webhook-secret-copy-${h.id}`}
                    onClick={() => { navigator.clipboard?.writeText(h.secret); toast.success(t("wh.secret_copied", "Signing secret copied")); }}
                    className="text-[10px] text-gray-400 hover:text-gray-700 flex items-center gap-0.5"><Copy size={10} /> secret</button>
                </div>
              </div>
              <button data-testid={`webhook-test-${h.id}`} onClick={() => test(h)}
                className="flex items-center gap-1 text-xs font-semibold text-blue-600 border border-blue-200 rounded-md px-2 py-1 hover:bg-blue-50 transition-colors"><PaperPlaneTilt size={12} /> {t("wh.send_test", "Send test")}</button>
              <button data-testid={`webhook-log-${h.id}`} onClick={() => toggleLog(h)}
                className="flex items-center gap-1 text-xs font-medium text-gray-500 border border-gray-200 rounded-md px-2 py-1 hover:bg-gray-50 transition-colors"><ListMagnifyingGlass size={12} /> {t("wh.log", "Log")}</button>
              <button data-testid={`webhook-delete-${h.id}`} onClick={() => del(h)}
                className="text-gray-400 hover:text-red-600 p-1"><Trash size={15} /></button>
            </div>
            {logFor === h.id && (
              <div className="mt-2 border-t border-gray-100 pt-2" data-testid={`webhook-deliveries-${h.id}`}>
                {logs.length === 0 && <p className="text-xs text-gray-400">{t("wh.no_deliveries", "No deliveries yet — hit \"Send test\".")}</p>}
                <div className="max-h-40 overflow-y-auto space-y-0.5">
                  {logs.map((d, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-gray-500">
                      <span className={d.ok ? "text-green-600" : "text-red-500"}>{d.ok ? "✓" : "✗"}</span>
                      <span className="font-mono">{d.event}</span>
                      <span>{d.ok ? `HTTP ${d.status_code}` : d.error}</span>
                      {d.dead && <span className="text-[10px] font-bold text-red-400 bg-red-50 rounded px-1">DEAD</span>}
                      <span className="ml-auto">{(d.ts || "").slice(0, 16).replace("T", " ")}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
        {hooks.length === 0 && <p className="text-sm text-gray-400" data-testid="webhooks-empty">{t("wh.no_hooks", "No webhooks yet.")}</p>}
      </div>
    </div>
  );
};
